import React, { useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";

// Components
import PopUp from "./PopUp";
import InfoCardActions, { resolveInfoCardActionLink } from "../Layouts/Content/InfoCardActions";

// Context
import { usePopup, PopupType } from "../../Context/PopupContext";
import { UserAuthenticationContext } from "../../Context/UserAuthenticationContext";

// Hooks, Models & Helpers
import useActivePopups from "../../Hooks/Popups/useActivePopups";
import type { ActivePopup } from "../../Models/DTOs/ActivePopupDTO";
import { dismissPopup } from "../../Helpers/PopupDismissal";

/**
 * Drives the data-driven (CMS-configured) popups end to end:
 *  - fetches the eligible popups for the current page (via useActivePopups),
 *  - honours openDelaySeconds before handing the winner to PopupContext (using
 *    its server priority so it can override lower-priority popups like search,
 *    or be suppressed by higher-priority ones like onboarding),
 *  - renders the winning popup (full card, image-only, or aspect-ratio sized)
 *    and records dismissals on close.
 */
const DataDrivenPopup: React.FC = () => {
    const location = useLocation();
    const { winner } = useActivePopups(location.pathname);
    const { activePopup, popupData, openPopup, closePopup } = usePopup();
    const authCtx = useContext(UserAuthenticationContext);
    const isLoggedIn = authCtx.doesAuthTokenExist();

    const isShowingDataDriven = activePopup === PopupType.DATA_DRIVEN;
    const openPopupId = isShowingDataDriven ? (popupData as ActivePopup)?.popupId : null;

    // Holds the pending open-delay timer so it can be cancelled both when the
    // winner changes and when the login state flips.
    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearOpenTimer = () => {
        if (openTimerRef.current !== null) {
            clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
    };

    // Logging in or out restarts the popup experience. Cancel any pending open
    // and close a popup left over from the previous auth state so the open-delay
    // countdown starts fresh from the moment of the transition. Without this a
    // logged-out-only popup could appear instantly on logout — its delay treated
    // as already elapsed because the user had been on the page long enough — and
    // the winner effect's "already showing" guard would skip re-arming for a
    // popup that stays eligible across the boundary. useActivePopups refetches on
    // the same login-state change and the winner effect then re-arms the delay.
    const prevLoggedInRef = useRef(isLoggedIn);
    useEffect(() => {
        if (prevLoggedInRef.current === isLoggedIn) return;
        prevLoggedInRef.current = isLoggedIn;

        clearOpenTimer();
        if (isShowingDataDriven) closePopup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // React to winner changes: open the winner (after its delay), or close a
    // stale data-driven popup when there's no longer an eligible one.
    useEffect(() => {
        clearOpenTimer();

        if (!winner) {
            if (isShowingDataDriven) closePopup();
            return;
        }

        // Already showing this popup — nothing to do.
        if (openPopupId === winner.popupId) return;

        const delayMs = Math.max(0, (winner.openDelaySeconds ?? 0) * 1000);

        if (delayMs === 0) {
            openPopup(PopupType.DATA_DRIVEN, winner, winner.priority);
            return;
        }

        openTimerRef.current = setTimeout(() => {
            openPopup(PopupType.DATA_DRIVEN, winner, winner.priority);
        }, delayMs);

        // Cancel the pending open if we navigate away before it fires.
        return () => clearOpenTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [winner]);

    // Lock body scroll while a data-driven popup is on screen.
    useEffect(() => {
        if (isShowingDataDriven) {
            document.body.classList.add("disable-scroll");
        } else {
            document.body.classList.remove("disable-scroll");
        }
        return () => {
            document.body.classList.remove("disable-scroll");
        };
    }, [isShowingDataDriven]);

    if (!isShowingDataDriven || !popupData) return null;

    const popup = popupData as ActivePopup;
    const card = popup.infoCard;

    const imageOnly = !!popup.contentDisabled;
    const aspectRatio = !!popup.useImageAspectRatio;
    const showContent = !imageOnly;
    // Aspect-ratio and image-only modes render a real <img> so the image's
    // intrinsic dimensions can drive sizing; the default mode uses a CSS bg.
    const renderImageElement = aspectRatio || imageOnly;

    const handleClose = () => {
        dismissPopup(popup);
        closePopup();
    };

    const containerClass = [
        "popup--data-driven",
        imageOnly ? "is-image-only" : "",
        aspectRatio ? "is-aspect-ratio" : "",
    ]
        .filter(Boolean)
        .join(" ");

    // When the card's content is hidden, the image itself becomes the CTA,
    // linking to the first resolvable action.
    const imageLink = imageOnly
        ? (card.actions ?? [])
              .map((a) => resolveInfoCardActionLink(a))
              .find((l): l is NonNullable<typeof l> => !!l) ?? null
        : null;

    const imageElement = card.bgImageUrl ? (
        <img className="popup--data-driven__image" src={card.bgImageUrl} alt={card.heading ?? ""} />
    ) : null;

    let imageNode: React.ReactNode = imageElement;
    if (imageElement && imageLink) {
        imageNode = imageLink.href ? (
            <a
                className="popup--data-driven__image-link"
                href={imageLink.href}
                target={imageLink.openInNewTab ? "_blank" : "_self"}
                rel={imageLink.openInNewTab ? "noopener noreferrer" : undefined}
                onClick={handleClose}
            >
                {imageElement}
            </a>
        ) : (
            <Link
                className="popup--data-driven__image-link"
                to={imageLink.to ?? "/"}
                target={imageLink.openInNewTab ? "_blank" : "_self"}
                onClick={handleClose}
            >
                {imageElement}
            </Link>
        );
    }

    return (
        <PopUp isOpen={true} canBeDismissed={true} isFullScreen={true} onClose={handleClose}>
            <div
                className={containerClass}
                style={
                    !renderImageElement && card.bgImageUrl
                        ? { backgroundImage: `url("${card.bgImageUrl}")` }
                        : undefined
                }
            >
                {renderImageElement ? imageNode : null}

                {showContent ? (
                    <>
                        <div className="popup--data-driven__overlay" />

                        <div className="popup--data-driven__content">
                            {card.brandingImageUrl ? (
                                <img
                                    className="popup--data-driven__branding"
                                    src={card.brandingImageUrl}
                                    alt=""
                                />
                            ) : null}

                            {card.heading ? <h2>{card.heading}</h2> : null}
                            {card.subHeading ? (
                                <p className="popup--data-driven__sub">{card.subHeading}</p>
                            ) : null}

                            {card.actions && card.actions.length > 0 ? (
                                <div className="popup--data-driven__actions">
                                    <InfoCardActions
                                        actions={card.actions}
                                        keyPrefix={card.infoCardId}
                                        onActionClick={handleClose}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </>
                ) : null}
            </div>
        </PopUp>
    );
};

export default DataDrivenPopup;
